import axios from 'axios';

import { getApiEndPoint } from '../helpers/apibase';
import { logger } from './loggerService';
import { mapServerError } from './utils/serverErrorMapper';

/**
 * AuthZ `GET /orgs/{orgId}/user/roles` (Portal: `/auth/orgs/.../user/roles`).
 * Only `orgId` and `roles` are always present; optional fields are omitted when empty.
 * Scope fields may include `"*"` for all resources. Portal ignores scope for now
 * (uses `roles` only). A user has at most one V2 role per org; Portal uses `roles[0]`.
 *
 * @example
 * { orgId: 'org1234', roles: ['Owner'], properties: ['*'], brands: ['brand567'] }
 * { orgId: 'org1234', roles: [] }
 */
export type GetOwnRolesResponse = {
  orgId: string;
  roles: string[];
  optionalPermissionSetKeys?: string[];
  properties?: string[];
  brands?: string[];
  salesChannels?: string[];
};

/** AuthZ V2 role names that are not (or not yet) Zeus teammate roles. */
export type AuthZV2AppAccessLevel = 'StaffMember' | 'Courier';

/**
 * Effective Portal role (Flipdish/Zeus string union + AuthZ-only roles).
 * Keep this string-based so `Permissions` stays assignable from role literals like `'Owner'`.
 */
export type PortalAppAccessLevel =
  NonNullable<Flipdish.App['AppAccessLevel']> | AuthZV2AppAccessLevel;

const AUTH_Z_V2_ROLE_NAMES = [
  'Owner',
  'ManagedOwner',
  'PropertyOwner',
  'PropertyManager',
  'FinanceManager',
  'Integrator',
  'StaffMember',
  'Courier',
] as const;

type AuthZRoleName = (typeof AUTH_Z_V2_ROLE_NAMES)[number];

/** AuthZ role name → Portal/Zeus AppAccessLevel (FinanceManager → Zeus FinanceManger typo). */
const AUTHZ_TO_PORTAL: Record<AuthZRoleName, PortalAppAccessLevel> = {
  Owner: 'Owner',
  ManagedOwner: 'ManagedOwner',
  PropertyOwner: 'PropertyOwner',
  PropertyManager: 'PropertyManager',
  FinanceManager: 'FinanceManger',
  Integrator: 'Integrator',
  StaffMember: 'StaffMember',
  Courier: 'Courier',
};

const userRolesApi = axios.create({
  baseURL: getApiEndPoint(),
  withCredentials: true,
});

export const getOwnRolesQueryKey = 'getOwnRoles';

export async function getOwnRoles(orgId: string): Promise<GetOwnRolesResponse> {
  try {
    const response = await userRolesApi.get<GetOwnRolesResponse>(
      `/auth/orgs/${encodeURIComponent(orgId)}/user/roles`
    );
    const data = response.data;
    // Product invariant: one V2 teammate role per user per org. Log if AuthZ returns more.
    if ((data.roles?.length ?? 0) > 1) {
      logger.warn('AuthZ returned multiple org roles for one user; expected at most one', {
        orgId: data.orgId ?? orgId,
        roles: data.roles,
      });
    }
    return data;
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

function mapAuthZRoleToAppAccessLevel(role: string): PortalAppAccessLevel | undefined {
  if ((AUTH_Z_V2_ROLE_NAMES as readonly string[]).includes(role)) {
    return AUTHZ_TO_PORTAL[role as AuthZRoleName];
  }
  return undefined;
}

export function pickPrimaryAppAccessLevel(
  roles: readonly string[],
  orgId?: string
): PortalAppAccessLevel | undefined {
  if (roles.length === 0) {
    logger.warn('AuthZ returned no org roles; leaving AppAccessLevel unset', {
      orgId,
      roles,
    });
    return undefined;
  }

  const primary = mapAuthZRoleToAppAccessLevel(roles[0]);
  if (primary === undefined) {
    logger.warn(
      'AuthZ returned org roles that Portal could not map; leaving AppAccessLevel unset',
      {
        orgId,
        roles,
      }
    );
  }

  return primary;
}

const userRolesService = {
  getOwnRoles,
  getOwnRolesQueryKey,
  pickPrimaryAppAccessLevel,
};

export default userRolesService;
